// ABOUTME: The lifecycle every periodic refresh in the app shares: start on mount, stop while the
// ABOUTME: tab is hidden, start again when it comes back — without an immediate run.
import { useEffect, useEffectEvent } from "react"

interface WhileVisibleOptions {
  enabled: boolean
  /** Restart when this changes — the interval, typically. */
  restartOn?: unknown
  start: () => void
  stop: () => void
}

/**
 * No immediate run when the tab becomes visible: iOS WebViews fire
 * `visibilitychange` spuriously (rotation, bottom sheets, the keyboard), and a
 * refresh per event would fan an N-week calendar out into N parallel requests
 * on a slow link. The timer that resumes here is refresh enough.
 *
 * `start`/`stop` are read through effect events, so a caller may pass inline
 * closures; what restarts the cycle is `enabled` and `restartOn`.
 */
export function useWhileVisible({ enabled, restartOn, start, stop }: WhileVisibleOptions): void {
  const startNow = useEffectEvent(start)
  const stopNow = useEffectEvent(stop)

  useEffect(() => {
    if (!enabled) {
      stopNow()
      return
    }
    const onVisibilityChange = () => {
      if (document.hidden) stopNow()
      else startNow()
    }

    // Not while hidden: `restartOn` reruns this effect whenever the interval
    // changes (the realtime connection coming and going), and starting here
    // would leave a background tab polling with no visibility event left to
    // stop it again.
    if (!document.hidden) startNow()
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      stopNow()
    }
  }, [enabled, restartOn])
}
